import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle, Navigation } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { CONTACT, IMG } from "../data/content";
import { api, track } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", message: "", website: "" });
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/callbacks", form);
      track("callback_request", {});
      setSent(true);
    } catch {
      toast.error("Something went wrong — please call or WhatsApp us directly.");
    }
  };

  const actions = [
    { icon: Phone, label: "Call Now", sub: CONTACT.phonePrimary, href: `tel:${CONTACT.phonePrimaryRaw}`, testid: "contact-call-btn", event: "call_click" },
    { icon: MessageCircle, label: "WhatsApp", sub: "Chat with our team", href: CONTACT.whatsapp, testid: "contact-whatsapp-btn", event: "whatsapp_click", external: true },
    { icon: Navigation, label: "Directions", sub: "Sri Akshita Oaks, Currency Nagar", href: CONTACT.directions, testid: "contact-directions-btn", event: "directions_click", external: true },
  ];

  return (
    <main>
      <PageHero
        eyebrow="Contact"
        title="Let’s Create Something People Will Remember"
        description="Call, WhatsApp or visit us — or request a callback and our event team will reach out."
        image={IMG.dining}
      />
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-2 gap-14">
          <div>
            <div className="grid gap-4">
              {actions.map((a) => (
                <Reveal key={a.label}>
                  <a
                    href={a.href}
                    {...(a.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    onClick={() => track(a.event, { location: "contact_page" })}
                    className="flex items-center gap-5 border border-[#C9A55C]/20 bg-[#0D1018] p-6 hover:border-[#C9A55C]/70 transition-colors duration-300 group"
                    data-testid={a.testid}
                  >
                    <span className="w-12 h-12 border border-[#C9A55C]/30 flex items-center justify-center text-[#C9A55C] group-hover:bg-[#C9A55C]/10 transition-colors">
                      <a.icon size={18} />
                    </span>
                    <span>
                      <span className="block font-display text-lg text-[#F3EEE5]">{a.label}</span>
                      <span className="block text-xs text-[#A9ABB2] mt-0.5">{a.sub}</span>
                    </span>
                  </a>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.1}>
              <div className="mt-8 space-y-4 text-sm text-[#A9ABB2]" data-testid="contact-details">
                <p className="flex items-center gap-3"><Phone size={14} className="text-[#C9A55C]" /> {CONTACT.phonePrimary} · Alternate: {CONTACT.phoneSecondary}</p>
                <p className="flex items-center gap-3"><Mail size={14} className="text-[#C9A55C]" /> {CONTACT.email}</p>
                <p className="flex items-start gap-3"><MapPin size={14} className="text-[#C9A55C] mt-1" /> {CONTACT.address}</p>
              </div>
              <Link to="/plan-your-event" className="btn-gold mt-8 inline-flex" data-testid="contact-brief-btn">Create Your Event Brief</Link>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="border border-[#C9A55C]/20 bg-[#0D1018] p-8 sm:p-10">
              <p className="text-xs uppercase tracking-[0.35em] text-[#C9A55C] mb-3">Request a Callback</p>
              <h2 className="font-display text-2xl text-[#F3EEE5]">We’ll Call You Back</h2>
              {sent ? (
                <p className="mt-6 text-sm text-[#A9ABB2]" data-testid="callback-success-message">
                  Thank you — our team will call you back shortly.
                </p>
              ) : (
                <form onSubmit={submit} className="mt-8 space-y-5" data-testid="callback-form">
                  <input className="lux-input" placeholder="Your Name *" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="callback-name-input" />
                  <input className="lux-input" placeholder="Phone Number *" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="callback-phone-input" />
                  <textarea className="lux-input min-h-[90px]" placeholder="What are you planning? (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="callback-message-textarea" />
                  <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex="-1" autoComplete="off" />
                  <button type="submit" className="btn-gold w-full justify-center" data-testid="callback-submit-btn">Request Callback</button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
